import { useState } from 'react'
import { Plus, Edit2, Trash2, Building2, MapPin, GitBranch, CreditCard } from 'lucide-react'
import OrgModal from './OrgModal'
import ManagePlanModal from './ManagePlanModal'
import Toast from '../common/Toast'
import ConfirmDialog from '../common/ConfirmDialog'

const PLAN_COLORS = {
  starter: 'bg-gray-100 text-gray-600',
  growth: 'bg-blue-100 text-blue-700',
  enterprise: 'bg-purple-100 text-purple-700',
}

// Trial/subscription chip for an org card: paying > trial countdown > lapsed.
function orgAccessChip(org) {
  if (org.subscriptionStatus === 'comped') return { label: 'Comped', cls: 'bg-purple-100 text-purple-700' }
  if (org.subscriptionStatus === 'active') return { label: 'Active', cls: 'bg-green-100 text-green-700' }
  if (org.subscriptionStatus === 'past_due') return { label: 'Past due', cls: 'bg-amber-100 text-amber-700' }
  if (org.subscriptionStatus === 'cancelled') return { label: 'Cancelled', cls: 'bg-red-100 text-red-700' }
  if (!org.trialEndsAt) return { label: 'No trial set', cls: 'bg-gray-100 text-gray-500' }
  const daysLeft = Math.ceil((org.trialEndsAt.getTime() - Date.now()) / 86_400_000)
  if (daysLeft < 0) return { label: `Trial expired ${-daysLeft}d ago`, cls: 'bg-red-100 text-red-700' }
  return { label: `Trial: ${daysLeft}d left`, cls: 'bg-blue-100 text-blue-700' }
}

export default function OrganizationsTab({ organizations, branches, onAdd, onUpdate, onDelete, onManagePlan }) {
  const [showModal, setShowModal] = useState(false)
  const [editingOrg, setEditingOrg] = useState(null)
  const [managingOrg, setManagingOrg] = useState(null)
  const [toast, setToast] = useState(null)
  const [confirmingDelete, setConfirmingDelete] = useState(null) // org object

  function openAdd() { setEditingOrg(null); setShowModal(true) }
  function openEdit(org) { setEditingOrg(org); setShowModal(true) }
  function closeModal() { setShowModal(false); setEditingOrg(null) }

  async function handleConfirmDelete() {
    const org = confirmingDelete
    setConfirmingDelete(null)
    const result = await onDelete(org.id)
    setToast(result?.success === false
      ? { message: result.message, type: 'error' }
      : { message: `"${org.name}" has been deleted.`, type: 'success' }
    )
  }

  async function handleSave(orgData, orgBranches) {
    if (editingOrg) {
      const result = await onUpdate(editingOrg.id, orgData, orgBranches)
      setToast(result?.success === false
        ? { message: result.message, type: 'error' }
        : { message: 'Organization updated successfully!', type: 'success' }
      )
    } else {
      const result = await onAdd(orgData, orgBranches)
      setToast(result?.success === false
        ? { message: result.message, type: 'error' }
        : { message: 'Organization created successfully!', type: 'success' }
      )
    }
    closeModal()
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">
          Organizations
          <span className="ml-2 text-sm font-normal text-gray-500">({organizations.length})</span>
        </h2>
        <button
          onClick={openAdd}
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition text-sm"
        >
          <Plus className="w-4 h-4" />
          <span>Add Organization</span>
        </button>
      </div>

      {/* Empty state */}
      {organizations.length === 0 && (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No Organizations Yet</h3>
          <p className="text-gray-400 text-sm mb-4">
            Create your first petroleum organization to get started.
          </p>
          <button
            onClick={openAdd}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition text-sm"
          >
            + Add Organization
          </button>
        </div>
      )}

      {/* Org cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {organizations.map((org) => {
          const orgBranches = branches.filter((b) => b.organizationId === org.id)
          return (
            <div key={org.id} className="bg-white rounded-xl shadow hover:shadow-md transition p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">{org.name}</h3>
                    <div className="flex items-center space-x-1 mt-0.5">
                      <MapPin className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">{org.location}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                  <button
                    onClick={() => setManagingOrg(org)}
                    className="p-2 hover:bg-purple-50 text-purple-600 rounded-lg transition"
                    title="Manage plan & trial"
                  >
                    <CreditCard className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openEdit(org)}
                    className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition"
                    title="Edit organization"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(org)}
                    className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition"
                    title="Delete organization"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Plan + access chips */}
              {(() => {
                const chip = orgAccessChip(org)
                return (
                  <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${PLAN_COLORS[org.plan] || PLAN_COLORS.starter}`}>
                      {org.plan || 'starter'}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${chip.cls}`}>
                      {chip.label}
                    </span>
                  </div>
                )
              })()}

              {org.description && (
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">{org.description}</p>
              )}

              <div className="flex items-center space-x-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                <GitBranch className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="font-medium">{orgBranches.length} branch{orgBranches.length !== 1 ? 'es' : ''}</span>
                {orgBranches.length > 0 && (
                  <span className="text-gray-400 truncate">
                    &mdash; {orgBranches.map((b) => b.name).join(', ')}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {managingOrg && (
        <ManagePlanModal
          org={managingOrg}
          branchCount={branches.filter((b) => b.organizationId === managingOrg.id).length}
          onSave={async (orgId, changes) => {
            const result = await onManagePlan(orgId, changes)
            if (result?.success !== false) {
              setToast({ message: `Plan & trial updated for "${managingOrg.name}".`, type: 'success' })
            }
            return result
          }}
          onClose={() => setManagingOrg(null)}
        />
      )}

      {showModal && (
        <OrgModal
          org={editingOrg}
          existingBranches={
            editingOrg ? branches.filter((b) => b.organizationId === editingOrg.id) : []
          }
          onSave={handleSave}
          onClose={closeModal}
        />
      )}

      <Toast toast={toast} onDismiss={() => setToast(null)} />

      {confirmingDelete && (() => {
        const branchCount = branches.filter((b) => b.organizationId === confirmingDelete.id).length
        return (
          <ConfirmDialog
            message={
              branchCount > 0
                ? `Delete "${confirmingDelete.name}"? This will also remove ${branchCount} branch(es). This cannot be undone.`
                : `Delete "${confirmingDelete.name}"? This cannot be undone.`
            }
            onConfirm={handleConfirmDelete}
            onCancel={() => setConfirmingDelete(null)}
          />
        )
      })()}
    </div>
  )
}
